import { Component } from '@theme/component';

let pdfModule;
const historyKey = 'tl-testing-history-v1';

/** Searches actual published report metadata and opens the unchanged laboratory documents. */
class TestingLibrary extends Component {
  requiredRefs = ['input', 'results', 'status', 'learn', 'recent', 'recentContent', 'history', 'historyList', 'dialog', 'pages', 'previewStatus', 'original', 'labels'];
  #events;
  #catalog;
  #model;
  #labels;
  #byId = new Map();
  #history = [];
  #rows = [];
  #active = -1;
  #timer;
  #epoch = 0;
  #loadingTask;
  #renderTask;
  #opener;
  #previousOverflow;

  connectedCallback() { super.connectedCallback(); this.#start(); }
  updatedCallback() { super.updatedCallback(); this.#start(); }
  disconnectedCallback() { super.disconnectedCallback(); this.#stop(); }

  async #start() {
    this.#stop();
    this.#events = new AbortController();
    const { signal } = this.#events;
    this.#catalog = undefined; this.#rows = []; this.#active = -1;
    this.refs.results.replaceChildren(); this.#hideResults();
    this.#labels = JSON.parse(this.refs.labels.textContent);
    this.refs.input.disabled = true;
    this.refs.status.classList.add('tl-testing__sr');
    this.refs.status.textContent = this.#labels.loading;
    this.refs.input.addEventListener('input', this.#input, { signal });
    this.refs.input.addEventListener('focus', () => { if (this.#rows.length) this.#showResults(); }, { signal });
    this.refs.input.addEventListener('keydown', this.#keyboard, { signal });
    document.addEventListener('pointerdown', event => {
      if (!this.contains(event.target)) this.#hideResults();
      if (!this.refs.learn.contains(event.target)) this.closeInfo();
    }, { signal });
    this.refs.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, { signal });
    this.refs.dialog.addEventListener('click', event => { if (event.target === this.refs.dialog) this.close(); }, { signal });
    try {
      const [response, model] = await Promise.all([
        fetch(this.dataset.catalog, { signal, credentials: 'omit' }), import(this.dataset.model)
      ]);
      if (!response.ok) throw new Error('Report index unavailable');
      const catalog = await response.json();
      if (signal.aborted || !this.isConnected) return;
      if (catalog.schema !== 2 || !Array.isArray(catalog.reports)) throw new Error('Report index format');
      this.#catalog = catalog;
      this.#model = model;
      this.#byId = new Map(catalog.reports.map(report => [report.id, report]));
      let saved;
      try { saved = JSON.parse(localStorage.getItem(historyKey) || '[]'); } catch { saved = []; }
      this.#history = this.#model.restoreHistory(saved, catalog);
      this.#paintHistory();
      this.refs.input.disabled = false;
      this.refs.status.textContent = '';
    } catch (error) {
      if (signal.aborted) return;
      this.refs.status.classList.remove('tl-testing__sr');
      this.refs.status.textContent = this.#labels.indexError;
    }
  }

  #stop() {
    this.#events?.abort(); clearTimeout(this.#timer); this.#cancelPreview();
    if (this.refs?.dialog?.open) this.refs.dialog.close();
    if (this.refs?.learn) this.refs.learn.open = false;
    this.#unlock();
  }

  /** Dismisses the source information panel with a keyboard-accessible close control. @param {Event} [event] */
  closeInfo(event) {
    if (!this.refs.learn.open) return;
    this.refs.learn.open = false;
    if (event?.type === 'click') this.refs.learn.querySelector('summary')?.focus({ preventScroll: true });
  }

  #input = () => {
    clearTimeout(this.#timer);
    if (!this.#catalog) return;
    const query = this.refs.input.value;
    this.#active = -1;
    if (query.trim().length < 2) {
      this.#rows = []; this.refs.results.replaceChildren(); this.#hideResults();
      this.refs.status.textContent = ''; return;
    }
    this.refs.status.textContent = this.#labels.searching;
    this.#timer = setTimeout(() => {
      this.#rows = this.#model.searchReports(this.#catalog, query);
      this.#paintResults();
    }, 250);
  };

  #paintResults() {
    const fragment = document.createDocumentFragment();
    for (const [index, report] of this.#rows.entries()) {
      const option = document.createElement('a');
      option.className = 'tl-testing__option'; option.href = report.url;
      option.target = '_blank'; option.rel = 'noopener'; option.tabIndex = -1;
      option.id = `${this.id}-result-${index}`; option.role = 'option';
      option.setAttribute('aria-selected', 'false'); option.dataset.reportId = report.id;
      option.setAttribute('on:click', '/view'); option.dataset.featured = String(report.featured);
      const product = this.#catalog.products[report.handle];
      if (product?.image) {
        const image = document.createElement('img'); image.src = product.image; image.alt = '';
        image.width = 30; image.height = 40; image.loading = 'lazy'; option.append(image);
      }
      const content = document.createElement('span'); content.className = 'tl-testing__option-copy';
      const title = document.createElement('span'); title.textContent = report.title;
      const type = document.createElement('strong'); type.textContent = report.type;
      const date = document.createElement('small'); date.textContent = report.dateLabel;
      const line = document.createElement('span'); line.className = 'tl-testing__option-title';
      line.append(title, document.createTextNode(' — '), type); content.append(line, date); option.append(content);
      fragment.append(option);
    }
    this.refs.results.replaceChildren(fragment);
    this.refs.status.textContent = this.#rows.length ? `${this.#labels.results}: ${this.#rows.length}` : this.#labels.empty;
    if (!this.#rows.length) {
      const empty = document.createElement('p'); empty.className = 'tl-testing__empty';
      empty.textContent = this.#labels.empty; this.refs.results.append(empty);
    }
    this.#showResults();
  }

  #showResults() { this.refs.results.hidden = false; this.refs.input.setAttribute('aria-expanded', 'true'); }
  #hideResults() {
    this.refs.results.hidden = true; this.refs.input.setAttribute('aria-expanded', 'false');
    this.refs.input.removeAttribute('aria-activedescendant'); this.#active = -1;
  }
  #keyboard = event => {
    if (event.key === 'Escape') { event.preventDefault(); this.#hideResults(); return; }
    if (!this.#rows.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
    event.preventDefault(); this.#showResults();
    if (event.key === 'Enter') {
      if (this.#active >= 0) this.#open(this.#rows[this.#active], this.refs.input);
      return;
    }
    this.#active = this.#active < 0 ? (event.key === 'ArrowDown' ? 0 : this.#rows.length - 1)
      : (this.#active + (event.key === 'ArrowDown' ? 1 : -1) + this.#rows.length) % this.#rows.length;
    for (const [index, option] of [...this.refs.results.querySelectorAll('[role=option]')].entries()) {
      option.setAttribute('aria-selected', String(index === this.#active));
      if (index === this.#active) {
        this.refs.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }
    }
  };

  /** @param {MouseEvent} event */
  view(event) {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('[data-report-id]');
    const report = link && this.#byId.get(link.dataset.reportId);
    if (!report) return;
    event.preventDefault(); this.#open(report, link);
  }

  async #open(report, opener) {
    let url;
    try { url = new URL(report.url); } catch { return; }
    if (url.protocol !== 'https:' || url.hostname !== 'cdn.shopify.com') return;
    this.closeInfo();
    this.#cancelPreview(); const epoch = this.#epoch;
    const fromHistory = !!opener?.closest?.('[ref=historyList]');
    this.#opener = opener;
    this.#hideResults(); this.#history = this.#model.addVisit(this.#history, report, this.#catalog);
    try { localStorage.setItem(historyKey, JSON.stringify(this.#history)); } catch { /* Reading reports still works if storage is unavailable. */ }
    this.#paintHistory();
    if (fromHistory) this.#opener = this.refs.historyList.querySelector(`[data-report-id="${CSS.escape(report.id)}"]`);
    this.refs.pages.replaceChildren();
    this.refs.original.href = report.url;
    this.refs.original.hidden = true;
    this.refs.previewStatus.textContent = this.#labels.loadingReport;
    this.refs.previewStatus.hidden = false;
    if (!this.refs.dialog.open) {
      this.#previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.refs.dialog.showModal();
    }
    try {
      pdfModule ||= import(this.dataset.pdfModule);
      const pdf = await pdfModule;
      if (epoch !== this.#epoch || !this.isConnected) return;
      pdf.GlobalWorkerOptions.workerSrc = this.dataset.pdfWorker;
      const fontBase = new URL('.', new URL(this.dataset.pdfFont, location.href)).href;
      const task = pdf.getDocument({ url: report.url, withCredentials: false, isEvalSupported: false, useWasm: false, standardFontDataUrl: fontBase });
      this.#loadingTask = task;
      const document = await task.promise;
      for (let number = 1; number <= document.numPages; number++) {
        if (epoch !== this.#epoch) return;
        const page = await document.getPage(number);
        if (epoch !== this.#epoch) return;
        const viewport = page.getViewport({ scale: 1.5 * Math.min(devicePixelRatio || 1, 2) });
        const canvas = window.document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width); canvas.height = Math.ceil(viewport.height);
        canvas.setAttribute('aria-label', `${this.#labels.reportPage} ${number}`);
        this.refs.pages.append(canvas);
        const render = page.render({ canvasContext: canvas.getContext('2d'), viewport });
        this.#renderTask = render; await render.promise;
        if (epoch !== this.#epoch) return;
        this.refs.previewStatus.hidden = true;
      }
    } catch (error) {
      if (epoch !== this.#epoch) return;
      this.refs.previewStatus.hidden = false;
      this.refs.previewStatus.textContent = this.#labels.previewError;
      this.refs.original.hidden = false;
    }
  }

  #cancelPreview() {
    this.#epoch++;
    this.#renderTask?.cancel(); this.#renderTask = undefined;
    void this.#loadingTask?.destroy().catch(() => {}); this.#loadingTask = undefined;
  }

  /** Closes the document without triggering any transaction or changing its contents. */
  close() {
    this.#cancelPreview();
    if (this.refs.dialog.open) this.refs.dialog.close();
    this.#unlock();
    const opener = this.#opener;
    (opener?.isConnected && opener.getBoundingClientRect().height && !opener.closest('[hidden]') ? opener : this.refs.input).focus({ preventScroll: true });
  }
  #unlock() {
    if (this.#previousOverflow !== undefined) {
      document.documentElement.style.overflow = this.#previousOverflow;
      this.#previousOverflow = undefined;
    }
  }

  #paintHistory() {
    const fragment = document.createDocumentFragment();
    for (const visit of this.#history) {
      const report = this.#byId.get(visit.id); if (!report) continue;
      const row = document.createElement('li');
      const info = document.createElement('span');
      const date = document.createElement('time'); date.dateTime = new Date(visit.time).toISOString();
      date.textContent = new Intl.DateTimeFormat('en-US').format(visit.time);
      const type = document.createElement('strong'); type.textContent = report.type;
      info.append(date, document.createTextNode(` — ${report.title} — `), type);
      const link = document.createElement('a'); link.href = report.url; link.target = '_blank'; link.rel = 'noopener';
      link.dataset.reportId = report.id; link.setAttribute('on:click', '/view'); link.textContent = this.#labels.openPdf + ' ⟶';
      row.append(info, link); fragment.append(row);
    }
    this.refs.historyList.replaceChildren(fragment); this.refs.history.hidden = !this.#history.length;
    const recent = this.#byId.get(this.#history[0]?.id);
    this.refs.recent.hidden = !recent;
    if (!recent) return;
    const product = this.#catalog.products[recent.handle];
    const destination = product?.productUrl?.startsWith('/products/') ? product.productUrl : undefined;
    const card = document.createElement(destination ? 'a' : 'div'); card.className = 'tl-testing__product-card';
    if (destination) card.href = destination;
    if (product?.image) {
      const image = document.createElement('img'); image.src = product.image; image.alt = '';
      image.width = 126; image.height = 126; card.append(image);
    }
    const copy = document.createElement('div');
    const title = document.createElement('h3'); title.textContent = product?.title || recent.title; copy.append(title);
    if (product?.summary) {
      const description = document.createElement('p'); description.className = 'tl-testing__product-summary';
      description.textContent = product.summary; copy.append(description);
    }
    if (destination) {
      const label = document.createElement('span'); label.className = 'tl-testing__product-cta';
      label.textContent = this.#labels.viewProduct + ' ⟶'; copy.append(label);
    }
    card.append(copy); this.refs.recentContent.replaceChildren(card);
  }
}

if (!customElements.get('tl-testing-library')) customElements.define('tl-testing-library', TestingLibrary);
